import type {
  ExpenseItemPage,
  ExpenseListSort,
  ExpenseItemWithRelations,
  ExpenseStatus,
  PaymentMethod,
  CatalogItem,
  CreateExpenseItemInput,
  UpdateExpenseItemInput,
  CreateExpensePaymentInput,
  ExpensePayment,
  CreateCatalogItemInput,
  UpdateCatalogItemInput,
  ExpenseCashFilter,
  InstallmentInput,
} from '@peles/shared';
import { apiClient } from './client';

export interface ExpenseConfig {
  statuses: ExpenseStatus[];
  paymentMethods: PaymentMethod[];
  catalogItems: CatalogItem[];
}

export async function getExpenseConfig(): Promise<ExpenseConfig> {
  const { data } = await apiClient.get<ExpenseConfig>('/expenses/config');
  return data;
}

export async function getExpenseItems(filters?: {
  eventId?: string;
  vendorId?: string;
}): Promise<ExpenseItemWithRelations[]> {
  const params: Record<string, string> = {};
  if (filters?.eventId) params.eventId = filters.eventId;
  if (filters?.vendorId) params.vendorId = filters.vendorId;
  const { data } = await apiClient.get<ExpenseItemWithRelations[]>('/expenses', { params });
  return data;
}

/** The filters, sort and page of the expenses list, as the server reads them. */
export interface ExpenseListParams {
  page: number;
  pageSize: number;
  eventIds?: string[];
  vendorId?: string;
  categoryIds?: string[];
  statusIds?: string[];
  q?: string;
  sort?: ExpenseListSort;
  dir?: 'asc' | 'desc';
  /** Booked lines only, or needs only; absent is both. */
  phase?: 'BOOKED' | 'SOURCING';
  /** Quick cash filters, any of which matches. */
  cash?: ExpenseCashFilter[];
}

/** One page of expenses, filtered, ordered and totalled by the server. */
export async function getExpenseItemPage(params: ExpenseListParams): Promise<ExpenseItemPage> {
  const query: Record<string, string> = {
    page: String(params.page),
    pageSize: String(params.pageSize),
  };
  if (params.eventIds?.length) query.eventIds = params.eventIds.join(',');
  if (params.vendorId) query.vendorId = params.vendorId;
  if (params.categoryIds?.length) query.categoryIds = params.categoryIds.join(',');
  if (params.statusIds?.length) query.statusIds = params.statusIds.join(',');
  if (params.q?.trim()) query.q = params.q.trim();
  if (params.sort) query.sort = params.sort;
  if (params.dir) query.dir = params.dir;
  if (params.phase) query.phase = params.phase;
  if (params.cash?.length) query.cash = params.cash.join(',');
  const { data } = await apiClient.get<ExpenseItemPage>('/expenses', { params: query });
  return data;
}

export async function getExpenseItem(id: string): Promise<ExpenseItemWithRelations> {
  const { data } = await apiClient.get<ExpenseItemWithRelations>(`/expenses/${id}`);
  return data;
}

export async function createExpenseItem(
  input: CreateExpenseItemInput,
): Promise<ExpenseItemWithRelations> {
  const { data } = await apiClient.post<ExpenseItemWithRelations>('/expenses', input);
  return data;
}

export async function updateExpenseItem(
  id: string,
  updates: UpdateExpenseItemInput,
): Promise<ExpenseItemWithRelations> {
  const { data } = await apiClient.patch<ExpenseItemWithRelations>(`/expenses/${id}`, updates);
  return data;
}

/**
 * Delete a line. A line with items is deleted only with a say over them —
 * moved to another line of the event, or deleted with it; otherwise the
 * server answers 409 `HAS_ITEMS` with how many there are (`hasItemsCount`).
 */
export async function deleteExpenseItem(
  id: string,
  items?: { moveItemsTo: string } | { deleteItems: true },
): Promise<void> {
  await apiClient.delete(`/expenses/${id}`, {
    params: items
      ? 'moveItemsTo' in items
        ? { moveItemsTo: items.moveItemsTo }
        : { deleteItems: 'true' }
      : undefined,
  });
}

/** How many items a refused delete said the line has; null for any other failure. */
export function hasItemsCount(err: unknown): number | null {
  const data = (
    err as {
      response?: { status?: number; data?: { error?: { code?: string; itemCount?: number } } };
    }
  )?.response;
  if (data?.status !== 409 || data.data?.error?.code !== 'HAS_ITEMS') return null;
  return data.data.error.itemCount ?? 0;
}

export async function addExpensePayment(
  expenseItemId: string,
  input: Omit<CreateExpensePaymentInput, 'expenseItemId'>,
): Promise<ExpensePayment> {
  const { data } = await apiClient.post<ExpensePayment>(
    `/expenses/${expenseItemId}/payments`,
    input,
  );
  return data;
}

/** Replace a booked line's payment schedule; an empty one clears it. */
export async function replaceInstallments(
  expenseItemId: string,
  installments: InstallmentInput[],
): Promise<ExpenseItemWithRelations> {
  const { data } = await apiClient.put<ExpenseItemWithRelations>(
    `/expenses/${expenseItemId}/installments`,
    { installments },
  );
  return data;
}

/** Record what is left on a line as one payment, worked out by the server. */
export async function markPaidInFull(
  expenseItemId: string,
  input: { paymentMethodId: string; paidAt?: string; notes?: string },
): Promise<ExpenseItemWithRelations> {
  const { data } = await apiClient.post<ExpenseItemWithRelations>(
    `/expenses/${expenseItemId}/mark-paid`,
    input,
  );
  return data;
}

export async function removeExpensePayment(
  expenseItemId: string,
  paymentId: string,
): Promise<void> {
  await apiClient.delete(`/expenses/${expenseItemId}/payments/${paymentId}`);
}

export async function createCatalogItem(input: CreateCatalogItemInput): Promise<CatalogItem> {
  const { data } = await apiClient.post<CatalogItem>('/expenses/catalog-items', input);
  return data;
}

export async function updateCatalogItem(
  id: string,
  updates: UpdateCatalogItemInput,
): Promise<CatalogItem> {
  const { data } = await apiClient.patch<CatalogItem>(`/expenses/catalog-items/${id}`, updates);
  return data;
}
